export function hasPreloadedValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

export function normalizeDateValue(value: unknown): string {
  if (!hasPreloadedValue(value)) return '';
  const str = String(value).trim();
  return str.includes('T') ? str.split('T')[0] : str;
}

export function normalizeNumberValue(value: unknown): string {
  if (!hasPreloadedValue(value)) return '';
  const raw = String(value).trim().replace(/\s/g, '');
  if (/^\d{1,3}([.,]\d{3})+$/.test(raw)) {
    return raw.replace(/[.,]/g, '');
  }
  return raw.replace(/,/g, '');
}

export function normalizeFieldValue(value: unknown, type?: string): unknown {
  if (!hasPreloadedValue(value)) {
    return type === 'number' || type === 'date' ? '' : value ?? '';
  }
  if (type === 'date') return normalizeDateValue(value);
  if (type === 'number') return normalizeNumberValue(value);
  return value;
}

export function coerceBooleanValue(value: unknown): boolean | null {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return true;
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return false;
  }
  return null;
}

export function normalizeSelectValue(value: unknown, options: any[]): unknown {
  const coerced = coerceSelectRawValue(value);

  if (!hasPreloadedValue(coerced) && coerced !== false && coerced !== 0) return '';
  if (!options?.length) return coerced;

  const hasBooleanOptions = options.some((opt) => typeof opt.value === 'boolean');
  if (hasBooleanOptions) {
    const boolValue = coerceBooleanValue(coerced);
    if (boolValue !== null) {
      return boolValue;
    }

    const normalizedSearch = String(coerced).trim().toUpperCase();
    if (normalizedSearch === 'SI' || normalizedSearch === 'SÍ') {
      return true;
    }
    if (normalizedSearch === 'NO') {
      return false;
    }
  }

  const matchByValue = options.find((opt) => opt.value == coerced);
  if (matchByValue) return matchByValue.value;

  const normalizedSearch = String(coerced).trim().toUpperCase();
  const matchByLabel = options.find(
    (opt) => String(opt.label ?? '').trim().toUpperCase() === normalizedSearch
  );
  if (matchByLabel) return matchByLabel.value;

  const matchByStringValue = options.find(
    (opt) => String(opt.value).trim().toUpperCase() === normalizedSearch
  );
  if (matchByStringValue) return matchByStringValue.value;

  return coerced;
}

export function parseSelectEmitValue(value: unknown, options: unknown): unknown {
  const optionsList = resolveOptions(options);
  const hasBooleanOptions = optionsList.some((opt) => typeof opt.value === 'boolean');

  if (hasBooleanOptions) {
    const boolValue = coerceBooleanValue(value);
    if (boolValue !== null) {
      return boolValue;
    }
  }

  return value;
}

export function resolveOptions(options: unknown): any[] {
  if (typeof options === 'function') {
    return (options as () => any[])() ?? [];
  }
  return (options as any[]) ?? [];
}

export function coerceSelectRawValue(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    return obj['id'] ?? obj['value'] ?? obj['code'] ?? value;
  }
  return value;
}

const FIELD_ALIASES: Record<string, string[]> = {
  specialProtecionProgram: ['specialProtectionProgram', 'specialProtectionProgramId'],
  address: ['addressFixed'],
};

export function resolveRawFieldValue(data: Record<string, unknown> | null | undefined, fieldName: string): unknown {
  if (!data) return undefined;

  const direct = coerceSelectRawValue(data[fieldName]);
  if (direct !== undefined && direct !== null && direct !== '') {
    return direct;
  }

  for (const alias of FIELD_ALIASES[fieldName] ?? []) {
    const aliasValue = coerceSelectRawValue(data[alias]);
    if (aliasValue !== undefined && aliasValue !== null && aliasValue !== '') {
      return aliasValue;
    }
  }

  return data[fieldName];
}

const PRELOAD_OPTION_LABEL_ALIASES: Record<string, string[]> = {
  specialProtecionProgram: [
    'specialProtecionProgramName',
    'specialProtectionProgramName',
    'specialProtectionProgramLabel',
  ],
};

export function mergeSelectOptionsWithPreload(
  options: { value: string | number | boolean; label: string }[],
  data: Record<string, unknown> | null | undefined,
  fieldName: string
): { value: string | number | boolean; label: string }[] {
  const preloaded = resolveRawFieldValue(data, fieldName);
  const coerced = coerceSelectRawValue(preloaded);

  if (!hasPreloadedValue(coerced) && coerced !== 0) {
    return options;
  }

  const optionValue = coerced as string | number | boolean;

  if (options.some((option) => option.value == optionValue)) {
    return options;
  }

  let label = '';
  for (const key of PRELOAD_OPTION_LABEL_ALIASES[fieldName] ?? []) {
    const aliasLabel = data?.[key];
    if (typeof aliasLabel === 'string' && aliasLabel.trim()) {
      label = aliasLabel.trim();
      break;
    }
  }

  if (!label && preloaded && typeof preloaded === 'object') {
    const obj = preloaded as Record<string, unknown>;
    label = String(obj['name'] ?? obj['label'] ?? '').trim();
  }

  if (!label) {
    label = `Opción ${coerced}`;
  }

  return [...options, { value: optionValue, label }];
}

export function buildPreloadedConfig<T extends Record<string, unknown>>(
  config: T,
  rawValue: unknown,
  type?: string
): T & { defaultValue: unknown; enabled: boolean; originalValue: unknown; isDirty: boolean; isValid: boolean } {
  const defaultValue = normalizeFieldValue(rawValue ?? config['defaultValue'], type);
  const hasValue = hasPreloadedValue(defaultValue);
  return {
    ...config,
    defaultValue,
    enabled: !hasValue,
    showBlocker: true,
    originalValue: defaultValue,
    isDirty: false,
    isValid: hasValue,
  };
}

export function buildPreloadedSelectConfig<T extends Record<string, unknown>>(
  config: T,
  rawValue: unknown,
  options?: unknown,
  data?: Record<string, unknown>
): T & { defaultValue: unknown; enabled: boolean; showBlocker: boolean; originalValue: unknown; isDirty: boolean; isValid: boolean } {
  const fieldName = String(config['formName'] ?? '');
  const resolvedRaw = data && fieldName
    ? resolveRawFieldValue(data, fieldName)
    : rawValue;
  const optionsList = resolveOptions(options ?? config['options']);
  const normalized = normalizeSelectValue(resolvedRaw ?? config['defaultValue'], optionsList);
  const hasValue = hasPreloadedValue(normalized) || normalized === false || normalized === 0;

  return {
    ...config,
    defaultValue: normalized,
    enabled: !hasValue,
    showBlocker: true,
    originalValue: normalized,
    isDirty: false,
    isValid: hasValue,
  };
}

export function resolveFieldEnabled(options: {
  showBlocker: boolean;
  lockTouched: boolean;
  currentEnabled: boolean;
  requestedEnabled: boolean | undefined;
  defaultValue: unknown;
  editedByUser: boolean;
}): boolean {
  if (options.showBlocker && options.lockTouched) {
    return options.currentEnabled;
  }

  const hasValue = hasPreloadedValue(options.defaultValue)
    || options.defaultValue === false
    || options.defaultValue === 0;

  if (options.showBlocker && !options.editedByUser && hasValue) {
    return false;
  }

  return options.requestedEnabled ?? true;
}
